import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_ANDROID_MOBILE, IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import { useIntersectionObserver } from 'reactApp/hooks/useInterSectionObserver';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { Product } from 'reactApp/types/Billing';
import { Button } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { useButtonAdditionalText } from 'reactApp/ui/TariffSectionNew/hooks/useButtonAdditionalText';
import { useSwitchProducts } from 'reactApp/ui/TariffSectionNew/hooks/useSwitchProducts';
import type { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { useProductPrice } from 'reactApp/ui/TariffsModal/TariffListCard/hooks/useProductPrice';
import { TariffPeriodSwitch } from 'reactApp/ui/TariffsModal/TariffPeriodSwitch/TariffPeriodSwitch';
import type { TariffByModalTab } from 'reactApp/ui/TariffsModal/TariffsModalContent.types';
import { FeaturesListMobile } from 'reactApp/ui/TariffsModalMobile/FeaturesListMobile/FeaturesListMobile';
import { useBlink } from 'reactApp/ui/TariffsModalMobile/hooks/useBlink';
import { useGetProductsByTab } from 'reactApp/ui/TariffsModalMobile/hooks/useGetProductsByTab';
import { TariffsListMobile } from 'reactApp/ui/TariffsModalMobile/TariffsListMobile/TariffsListMobile';
import { sendDwh } from 'reactApp/utils/ga';
import { isTransactPeriod } from 'reactApp/utils/Period';

import styles from './TariffsModalContentMobile.css';

interface TariffsModalMobileProps {
    title: string;
    className?: string;
    titleClassName?: string;
    activeTab: ETabsName;
    setActiveTab: (tab: ETabsName) => void;
    tariffs: TariffByModalTab[];
    yearTariffsCount: number;
    monthTariffsCount: number;
    onBuyClick: (product: Product) => void;
    animate?: boolean;
    isIframe?: boolean;
    scrollRef?: RefObject<HTMLDivElement>;
    qaId?: string;
}

interface TariffModalButtonProps {
    tariff: TariffByModalTab;
    onClick: () => void;
    activeTab: ETabsName;
    label: number;
    qaId?: string;
}

const ANIMATION_DELAY_MS = 120;

const eventCategory = 'paywall';
const name = 'mnz_business_offers';
const platform = IS_IOS_MOBILE ? 'ios' : IS_ANDROID_MOBILE ? 'android' : 'touch';

const TariffModalButton = ({ tariff, onClick, activeTab, label, qaId }: TariffModalButtonProps) => {
    const { product, isYearFallbackProduct } = useSwitchProducts({ products: tariff.products, activeTab });
    const { priceText, transactText } = useProductPrice({ product });
    const buttonAdditionalText = useButtonAdditionalText(product);

    let title = priceText;

    if (isYearFallbackProduct) {
        title = 'Доступен в годовом плане';
    }
    if (isTransactPeriod(product.period)) {
        title = transactText;
    }

    return (
        <Button
            className={styles.button}
            mode={'primary'}
            theme={'vk'}
            middle
            onClick={onClick}
            disabled={isYearFallbackProduct}
            qaId={qaId && `${qaId}-buy-btn`}
        >
            <div key={`tariff-modal-button-text-${label}`}>
                <Text className={styles.buttonTitle} data-qa-id={qaId && `${qaId}-buy-btn-title`}>
                    {title}
                </Text>
                {buttonAdditionalText && !isYearFallbackProduct && (
                    <Text className={styles.buttonSubtitle} data-qa-id={qaId && `${qaId}-buy-btn-subtitle`}>
                        {buttonAdditionalText}
                    </Text>
                )}
            </div>
        </Button>
    );
};

export const TariffsModalContentMobile = ({
    tariffs,
    title,
    yearTariffsCount,
    monthTariffsCount,
    activeTab,
    setActiveTab,
    scrollRef,
    onBuyClick,
    animate,
    qaId,
}: TariffsModalMobileProps) => {
    const { hasError } = useSelector(ProductsSelectors.getLifeCycleState);
    const [tariff, setTariff] = useState<TariffByModalTab | null>(null);
    const [product, setProduct] = useState<Product | null>(null);
    const [navbarHeaderVisible, setNavbarHeaderVisible] = useState(false);
    const [featuresListVisible, setFeaturesListVisible] = useState(true);
    const [buttonKey, setButtonKey] = useState<number>(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const productsIds = useGetProductsByTab(tariffs, activeTab);

    const dwhData = {
        name,
        selectedPeriod: activeTab === 'month' ? 'm' : 'y',
        tariff_list: productsIds,
        platform,
    };

    useEffect(() => {
        sendDwh({
            eventCategory,
            action: 'show',
            dwhData,
        });

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const onButtonClick = useCallback(() => {
        if (product) {
            sendDwh({
                eventCategory,
                action: 'click',
                dwhData: {
                    ...dwhData,
                    tariff: product.id,
                },
            });

            onBuyClick(product);
        }
    }, [product]);

    const onSwitchClick = useCallback(
        (newTab: ETabsName) => {
            sendDwh({
                eventCategory,
                action: 'click-button',
                dwhData: {
                    ...dwhData,
                    button_name: 'change_period',
                },
            });
            setActiveTab(newTab);
            setButtonKey((prev) => prev + 1);
        },
        [setActiveTab]
    );

    const blinkFeaturesVisibility = useBlink(ANIMATION_DELAY_MS, setFeaturesListVisible);
    const animateFeatureList = useCallback(() => blinkFeaturesVisibility(false, true), [blinkFeaturesVisibility]);

    const onRowClick = (newTariff: TariffByModalTab, newProduct: Product) => {
        sendDwh({
            eventCategory,
            action: 'click-button',
            dwhData: {
                ...dwhData,
                button_name: 'change_tariff',
                tariff_from: product?.id,
                tariff_to: newProduct.id,
            },
        });

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (tariff?.tariffId !== newTariff?.tariffId || product?.id !== newProduct.id) {
            if (animate) {
                animateFeatureList();
            }

            timeoutRef.current = setTimeout(() => {
                setTariff(newTariff);
                setProduct(newProduct);
                timeoutRef.current = null;
            }, ANIMATION_DELAY_MS / 2);

            setButtonKey((prev) => prev + 1);
        }
    };

    const observerRef = useIntersectionObserver({
        onIntersecting: () => {
            if (animate) {
                setNavbarHeaderVisible(false);
            }
        },
        onIntersectingOut: () => {
            if (animate) {
                setNavbarHeaderVisible(true);
            }
        },
        rootMargin: '-68px',
    });

    return (
        <div className={styles.wrapper}>
            <Content hasError={hasError} isPage wrapClass={styles.root}>
                <Text
                    className={classNames(styles.hiddenTitle, {
                        [styles.visible]: navbarHeaderVisible,
                    })}
                    data-qa-id={qaId && `${qaId}-navbar-title`}
                    data-visible={String(navbarHeaderVisible)}
                >
                    {title}
                </Text>
                <div className={styles.scrollableContainer} ref={scrollRef} data-qa-id={`${qaId}-scrollable`}>
                    <div className={styles.header}>
                        <div
                            ref={observerRef}
                            className={classNames(styles.title, {
                                [styles.visible]: !navbarHeaderVisible,
                            })}
                            data-qa-id={qaId && `${qaId}-title`}
                            data-visible={String(!navbarHeaderVisible)}
                        >
                            {title}
                        </div>
                        <Spacing size={24} />
                        <FeaturesListMobile
                            tariff={tariff}
                            qaId={qaId}
                            className={classNames(styles.featuresList, {
                                [styles.visible]: featuresListVisible,
                            })}
                        />
                        <Spacing size={32} />
                    </div>
                    {Boolean(yearTariffsCount && monthTariffsCount) && (
                        <>
                            <div data-qa-id={qaId && `${qaId}-switch`} data-period={activeTab}>
                                <TariffPeriodSwitch checkedOption={activeTab} onClickSwitch={onSwitchClick} isMobile />
                            </div>
                            <Spacing size={24} />
                        </>
                    )}
                    <TariffsListMobile
                        tariffs={tariffs}
                        activeTab={activeTab}
                        setTariff={setTariff}
                        setProduct={setProduct}
                        onRowClick={onRowClick}
                        dataQa={qaId && `${qaId}-card`}
                    />
                    <Spacing size={44} />
                </div>
                <Spacing size={64} />
                {tariff && (
                    <TariffModalButton tariff={tariff} onClick={onButtonClick} activeTab={activeTab} label={buttonKey} qaId={qaId} />
                )}
                {!tariff && (
                    <Button className={styles.button} mode={'primary'} theme={'vk'} middle loading qaId={qaId && `${qaId}-buy-btn`}>
                        ...
                    </Button>
                )}
                <Spacing size={8} />
            </Content>
        </div>
    );
};
